/**
 * An agent's personal link from its members row's menu (`personalAction` says which): making it someone's (you, or for
 * admins anyone: a dialog asks whom) or taking it away (them, or admins: after asking).
 */
import { useState } from "preact/hooks";
import { api } from "../../api/client.ts";
import type { Member, PersonalAgent, Role } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { grantableTo, type PersonalAction, personalOf } from "../../lib/personalAgents.ts";
import { useAction } from "../../lib/resource.ts";
import { me } from "../../lib/state.ts";
import { toast } from "../../lib/toast.ts";
import { Button } from "../../ui/Button.tsx";
import { ConfirmDialog, Dialog, DialogActions } from "../../ui/Dialog.tsx";
import { Field, Form, Select } from "../../ui/form.tsx";

interface Props {
  org: string;
  role: Role;
  agent: Member;
  members: readonly Member[];
  onChange: () => void;
  onClose: () => void;
}

/** Choosing who an agent works for (you, or for admins anyone). */
function MakePersonal(p: Props) {
  const self = me.value?.id ?? "";
  const people = grantableTo(p.members, self, p.role, p.agent);
  const [person, setPerson] = useState(people.some((m) => m.id === self) ? self : (people[0]?.id ?? ""));
  const [make, busy] = useAction(async () => {
    await api.post(
      `/orgs/${p.org}/personal-agents`,
      person === self ? { agent: p.agent.id } : { agent: p.agent.id, person },
    );
    const name = people.find((m) => m.id === person)?.name ?? "";
    toast(t("personal.made", { agent: p.agent.name, person: name }));
    p.onClose();
    p.onChange();
  });
  return (
    <Form class="flex flex-col gap-4" onSubmit={make}>
      <Field label={t("personal.person")} hint={t("personal.hint")}>
        <Select value={person} onValue={setPerson} options={people.map((m) => ({ value: m.id, label: m.name }))} />
      </Field>
      <DialogActions>
        <Button answer="cancel" onClick={p.onClose}>
          {t("common.cancel")}
        </Button>
        <Button answer="confirm" variant="primary" submit={true} busy={busy}>
          {t("personal.make")}
        </Button>
      </DialogActions>
    </Form>
  );
}

/** The dialog for `open` (none: closed): whom the agent works for, or removing that after asking. */
export function PersonalDialogs(
  p: Props & { open: PersonalAction | null; links: readonly PersonalAgent[] | undefined },
) {
  const link = personalOf(p.links, p.agent.id);
  const [remove, removing] = useAction(async () => {
    await api.del(`/orgs/${p.org}/personal-agents/${p.agent.id}`);
    toast(t("personal.removed"));
    p.onClose();
    p.onChange();
  });
  return (
    <>
      <Dialog open={p.open === "make"} onClose={p.onClose} title={t("personal.makeTitle", { agent: p.agent.name })}>
        {p.open === "make" ? <MakePersonal {...p} /> : null}
      </Dialog>
      {link ? (
        <ConfirmDialog
          open={p.open === "remove"}
          onClose={p.onClose}
          title={t("personal.removeTitle")}
          message={t("personal.removeMessage", { agent: link.agent_name, person: link.person_name })}
          confirmLabel={t("personal.remove")}
          onConfirm={() => (removing ? undefined : remove())}
        />
      ) : null}
    </>
  );
}
